import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './CategoryRail.css';
import { ChannelCardSkeleton } from './common/SkeletonLoaders';
import ChannelCard from './ChannelCard';
import { getSlidesToShow, RAIL_BREAKPOINTS } from '../config/categoryRailSettings';

/** One rail: a `/v1/home` rail (`homeToCategories`) or a personal list, rendered once visible. */
const CategoryRail = React.memo(({ category, forceTextTitle = false }) => {
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const railRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 },
    );
    const current = railRef.current;
    if (current) {
      observer.observe(current);
      return () => observer.unobserve(current);
    }
    return undefined;
  }, []);

  const channels = category?.channels || [];

  if (!category) {
    return (
      <div className="my-8 category-rail" ref={railRef}>
        <div className="px-2 text-white/50">Category data unavailable</div>
      </div>
    );
  }

  const title = category.display_name || category.name || 'Untitled Category';

  const getCurrentSlidesToShow = () => {
    const width = window.innerWidth;
    const type = category?.style === 'top-number-poster' ? 'poster' : 'regular';
    return getSlidesToShow(width, type);
  };

  // Continue watching opens My Lists; a /v1/home rail opens its own page. The other personal
  // rails have no list page.
  const isContinueWatching =
    category._id === 'continue-watching' || category.style === 'continue-watching';
  const seeAllPath = isContinueWatching
    ? '/my-lists/continue-watching'
    : category.v1
      ? `/rail/${category._id}`
      : null;
  // Worth a page when the cards overflow the row, or the home rail stopped at its cap (the rail
  // itself may hold more).
  const shouldShowSeeAll = () =>
    seeAllPath &&
    (channels.length > Math.floor(getCurrentSlidesToShow()) ||
      (category.v1 && channels.length >= category.max_items));

  return (
    <div
      className={`category-rail relative ${
        channels.length <= Math.floor(getCurrentSlidesToShow()) ? 'rail-insufficient-items' : ''
      }`}
      ref={railRef}
    >
      <div className="flex items-center justify-between mb-4">
        {/* Category logo or text title */}
        {category.logo ? (
          <div className="category-logo-container">
            <img
              src={`${category.logo}/300/50`}
              alt={title}
              className="category-logo h-8 md:h-10 lg:h-12 max-w-[200px] md:max-w-[250px] lg:max-w-[300px] object-contain"
              srcSet={`
                ${category.logo}/120/20 120w,
                ${category.logo}/180/30 180w,
                ${category.logo}/240/40 240w,
                ${category.logo}/300/50 300w,
                ${category.logo}/360/60 360w,
                ${category.logo}/420/70 420w
              `}
              sizes="(max-width: 767px) 120px, (max-width: 1023px) 180px, (max-width: 1366px) 240px, 300px"
              onError={e => {
                e.target.style.display = 'none';
                if (e.target.nextElementSibling) {
                  e.target.nextElementSibling.style.display = 'block';
                }
              }}
            />
            <h2
              className={`font-bold text-white font-display uppercase ${
                forceTextTitle ? 'mt-2 block' : 'hidden'
              }`}
            >
              {title}
            </h2>
          </div>
        ) : (
          <h2 className="font-bold text-white font-display uppercase">{title}</h2>
        )}
        {shouldShowSeeAll() && (
          <button
            className="text-sm text-white/70 hover:text-white"
            onClick={() => navigate(seeAllPath)}
          >
            See All
          </button>
        )}
      </div>

      <div>
        <Slider
          dots={false}
          infinite={!hasBeenVisible ? false : channels.length > 5}
          speed={500}
          slidesToShow={getSlidesToShow(
            window.innerWidth,
            category?.style === 'top-number-poster' ? 'poster' : 'regular',
          )}
          slidesToScroll={3}
          arrows={!hasBeenVisible ? false : true}
          responsive={RAIL_BREAKPOINTS.map(bp => ({
            breakpoint: bp.breakpoint,
            settings: {
              slidesToShow: bp[category?.style === 'top-number-poster' ? 'poster' : 'regular'],
              slidesToScroll: 1,
            },
          }))}
        >
          {(() => {
            if (!hasBeenVisible) {
              const getSkeletonCount = () => {
                const width = window.innerWidth;
                const type = category?.style || 'regular';
                return Math.ceil(getSlidesToShow(width, type));
              };

              return Array.from({ length: getSkeletonCount() }).map((_, index) => (
                <div key={`skeleton-${index}`} className="px-2">
                  <ChannelCardSkeleton isPosterFormat={category?.style === 'top-number-poster'} />
                </div>
              ));
            } else {
              return channels.map((channel, index) => (
                <div key={channel?._id || index} className="px-2">
                  <ChannelCard channel={channel || {}} style={category?.style} index={index} />
                </div>
              ));
            }
          })()}
        </Slider>
      </div>
    </div>
  );
});

export default CategoryRail;
